import { initializeScrollManager, destroyScrollManager, initializeLenisAnchorScrolling, getLenisInstance } from './core/scroll-manager.js';
import { initializeScrollProgressIndicators } from './core/scroll-utils.js';
import { initializeScrollEffects, destroyScrollEffects } from './core/scroll-effects.js';
import { initializeParallax, destroyParallax } from './core/parallax.js';
import { initializeVideoCoverResizerListeners, resizeIframesToCover } from './core/video-cover-resizer.js';
import { querystringDragging } from './core/url-utils.js';
import { initializeDomLoadListeners } from './core/dom-load-listeners.js';
import { loadWebFonts } from './core/web-fonts.js';
import { updateNavMenuClasses } from './core/nav-menu-updater.js';
import { trackPageView } from './vendor/google-analytics.js';
import { initializeInteractiveHeader } from './ui/header/interactive-header.js';
import { initializeStickyHeader } from './ui/header/sticky.js';
import { initializeVideoPlayers, destroyVideoPlayers } from './ui/video/slideshow-loader.js';
import { slideshows, destroySlideshows } from './ui/slideshow-manager.js';
import { barbaPreventHandler, syncManualBodyClasses, syncPageStyles, syncPageScriptsByLocation, syncPageModals, fadeIn, fadeOut } from './vendor/barba.js';
import { initializeVideoPopupHandler } from './ui/video/modal-video-loader.js';
import { googleMaps } from './integrations/google-maps-manager.js';
import { initializeModals, destroyModals } from './ui/modal-manager.js';
import { initializeImageZoom } from './ui/image-zoom.js';
import { initializeBookingForms } from './ui/booking-form.js';
import { initializePropertyListShowMore } from './features/property-list-show-more.js';
import MicroModal from 'micromodal';
import barba from '@barba/core';
// Optional: If you use CSS classes for transitions via @barba/css
// import barbaCss from '@barba/css';

// --- Global Settings ---
const DEBUG_MODE = false; // Set to false for production to disable console logs.

// Configuration for querystringDragging
const querystringDraggingConfig = {
    domains: [
        window.location.hostname, // Automatically include the current site's domain
        // 'another-site.com.au', // Example: Add other domains if you cross-link and want to decorate them
    ],
    params: ['utm_medium', 'utm_source', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid']
};

// --- Web Font Configuration ---
// Define your font families here. These will be passed to the web-fonts loader.
// !!!NOTE!!! Use the family name and not the font name
const selfHostedFontConfig = [
    { family: 'Conneqt', weight: '400', style: 'normal' },
    { family: 'Conneqt', weight: '700', style: 'normal' },
    { family: 'Graphik', weight: '400', style: 'normal' },
    { family: 'Graphik', weight: '700', style: 'normal' },
];

const adobeFontConfig = [
  // { family: 'adobe-font-family-one' }, // Observer will try to load any weight/style
];

// --- Global State ---
let lenisInstance; // Keep a single Lenis instance across page transitions

/**
 * Manually parses and executes Gravity Forms inline scripts from the new page HTML.
 * This ensures forms are initialized correctly after Barba transitions.
 * @param {string} html 
 */
function refreshGravityForms(html) {
    if (!html) return;
    
    // We only want to run this if Gravity Forms is actually present on the page
    if (html.indexOf('gform_wrapper') === -1 && html.indexOf('gform_fields') === -1) {
        return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const scripts = doc.querySelectorAll('script');

    scripts.forEach(script => {
        const content = script.textContent;
        // Look for inline scripts that appear to be Gravity Forms initialization logic
        if (!script.src && (content.includes('gform.initializeOnLoaded') || content.includes('gf_global') || content.includes('gform_post_render'))) {
            try {
                window.eval(content);
            } catch (err) {
                console.warn('Gravity Forms Refresh Error:', err);
            }
        }
    });
}

/**
 * Initializes all page-specific JavaScript that needs to run after
 * new content is loaded by Barba.js.
 * @param {object} data - Barba transition data object.
 */
function initializePageScripts(data) {
    if (DEBUG_MODE) console.log('Barba: Initializing scripts for new page:', data.next.namespace);

    // Update nav menu classes for the new page
    updateNavMenuClasses(data.next.url.href);

    // The Lenis instance is now persistent. We just need to re-initialize components that use it.
    if (lenisInstance) {
        initializeScrollProgressIndicators('[data-js-scroll-distance="1"]', lenisInstance);
        initializeLenisAnchorScrolling(lenisInstance); // Re-attaches click listeners if needed, though a single global one is better.
        initializeStickyHeader(lenisInstance);
    }

    // Initialize scroll-triggered effects like class toggles and background color changes.
    initializeScrollEffects();

    // Initialize parallax effects for elements with the .parallax class
    initializeParallax();

    // Initialize modals for the new page
    initializeModals(DEBUG_MODE);

    // Decorate links with query parameters
    querystringDragging(
        querystringDraggingConfig.domains,
        querystringDraggingConfig.params,
        DEBUG_MODE
    );

    // IMPORTANT: Re-initialize other plugins and custom scripts here.
    // This is highly specific to your theme and plugins.
    // Examples:
    // if (typeof AOS !== 'undefined') { AOS.refreshHard(); }
    // if (jQuery && jQuery.fn.slick) { jQuery('.my-slider', data.next.container).slick(); }
    // if (window.myCustomGalleryInit) { window.myCustomGalleryInit(data.next.container); }

    // Apply 'cover' resizing to any video embeds on the new page.
    resizeIframesToCover('.video-container');

    // Initialize video players for fade-in effect.
    initializeVideoPlayers(data.next.container);

    // Initialize booking forms (header, shortcodes)
    initializeBookingForms();

    // --- Slideshow Initialization ---
    // Get page-specific slideshow configurations from data passed via inline script
    const pageSlideshowConfigs = window.pageSpecificData?.slideshows || [];

    // Initialize Slideshows if configs exist
    if (pageSlideshowConfigs.length > 0) {
        slideshows(pageSlideshowConfigs, DEBUG_MODE);
    } else if (DEBUG_MODE && document.querySelector('.swiper')) {
        // This is a bit broad, but if a swiper container exists but no config, it might be worth a log.
        console.log('Slideshows: .swiper element found, but no page-specific configurations were provided.');
    }

    // --- Google Maps Initialization ---
    // Prepare global settings from localized data
    const googleMapsGlobalSettings = {
        apiKey: window.app?.settings?.global_google_maps_api_key || '',
        // Safely parse styles, defaulting to an empty array
        defaultStyles: JSON.parse(window.app?.settings?.googleMapsDefaultSettings?.stylesString || '[]'),
        defaultMarker: window.app?.settings?.googleMapsDefaultSettings?.marker || null,
        // Configuration for resizing map markers on different screen sizes.
        responsiveMarkerConfig: {
            // Breakpoints should match your SCSS variables.
            breakpoints: {
                xs: 640,
                md: 860,
            },
            // Scaling factors for markers at each breakpoint.
            scales: {
                large: 1.0,   // Desktop (original size)
                medium: 0.7, // Tablet
                small: 0.7    // Mobile
            }
        }
    };

    // Get page-specific map configurations from data passed via inline script
    const pageMapConfigs = window.pageSpecificData?.google_maps_page || {};

    // Initialize Google Maps if an API key is present and map configurations exist
    if (googleMapsGlobalSettings.apiKey && Object.keys(pageMapConfigs).length > 0) {
        googleMaps(pageMapConfigs, googleMapsGlobalSettings, DEBUG_MODE);
    } else if (DEBUG_MODE && document.querySelector('.google-map__canvas') && !googleMapsGlobalSettings.apiKey) {
        console.warn('Google Maps: API key is missing from global settings. Maps will not be initialized.');
    }
}

/**
 * Cleans up scripts and instances before a page transition.
 * @param {object} data - Barba transition data object.
 */
function cleanupPageScripts(data) {
    if (DEBUG_MODE) console.log('Barba: Cleaning up scripts from old page:', data.current.namespace);
    // We no longer destroy Lenis on every transition.
    // destroyScrollManager();
    destroyScrollEffects(); // Disconnects IntersectionObservers for scroll effects
    destroyVideoPlayers(data.current.container); // Clean up video players to prevent memory leaks
    destroySlideshows(DEBUG_MODE); // Destroy all swiper instances
    destroyParallax(); // Disconnects observers and cleans up parallax elements
    destroyModals(); // Resets modal state for clean re-initialization

    // IMPORTANT: Destroy other plugin instances if they need manual cleanup.
    // Examples:
    // if (jQuery && jQuery.fn.slick && jQuery('.my-slider', data.current.container).hasClass('slick-initialized')) {
    //   jQuery('.my-slider', data.current.container).slick('unslick');
    // }
}

document.addEventListener('DOMContentLoaded', () => {
    // --- Initialize Persistent Services ---

    // Initialize Lenis once and for all.
    // Adjust the `lerp` value to change scroll speed. Lower is smoother/slower, higher is faster. Default is 0.1.
    lenisInstance = initializeScrollManager({
        lerp: 0.2
    });
    if (lenisInstance) {
        // Anchor scrolling can be initialized once globally.
        initializeLenisAnchorScrolling(lenisInstance);
    }

    // Initialize DOM listeners that should only run once.
    initializeDomLoadListeners();
    // Initialize interactive header elements. Since the header is persistent and not
    // replaced by Barba, this only needs to run once on initial page load.
    initializeInteractiveHeader();

    // Initialize the image zoom dialog listeners.
    initializeImageZoom();

    // Initialize booking forms on initial load.
    //initializeBookingForms();

    // Initialize all modal functionality, including click triggers and auto-triggers.
    initializeModals(DEBUG_MODE);

    // Initialize the video popup handler.
    initializeVideoPopupHandler();

    // Initialize the "Show more" toggle for grouped property lists (delegated,
    // so it survives AJAX/Barba re-renders of the list).
    initializePropertyListShowMore();

    // Initialize global event listeners for resizing videos.
    initializeVideoCoverResizerListeners('.video-container');

    // Load fonts first
    loadWebFonts(selfHostedFontConfig, adobeFontConfig).finally(() => {

        // --- Barba.js Setup ---

        // Optional: Use @barba/css for class-based transitions
        // barba.use(barbaCss);

        barba.init({
            sync: true, // Syncs body classes and page title from the fetched page
            debug: DEBUG_MODE,
            timeout: 7000, // Default timeout for transitions
            prevent: barbaPreventHandler,
            transitions: [{
                name: 'default',
                once(data) {
                    if (DEBUG_MODE) {
                        console.log('[Barba Debug] once: once event');
                    }
                    initializePageScripts(data); // Initialize scripts on first page load
                    // Manually track the initial page view since automatic tracking will be disabled.
                    trackPageView(false, DEBUG_MODE);
                },
                async leave(data) {
                    // Dispatch a custom event to notify other modules (like the header) that a page transition is starting.
                    document.dispatchEvent(new CustomEvent('barba-leave-start', { detail: data }));

                    // Hide the current content immediately to prevent it from being visible during the transition.
                    data.current.container.style.display = 'none';

                    // Create a promise that resolves after a minimum duration (e.g., 500ms)
                    const minDuration = new Promise(resolve => {
                        setTimeout(resolve, 500);
                    });

                    // Add class to body to show the page loader
                    document.body.classList.add('is-loading');
                    await minDuration; // Wait for at least 500ms

                    cleanupPageScripts(data);
                },
                beforeEnter(data) {
                    if (DEBUG_MODE) {
                        console.log('[Barba Debug] beforeEnter: document.body.className AT START of beforeEnter:', document.body.className);
                        console.log('[Barba Debug] beforeEnter: Next page raw HTML received by Barba:', data.next.html.substring(0, 1000) + '...'); // Log first 1000 chars
                    }

                    // Barba's `sync: true` handles body classes. These manual syncs ensure
                    // assets that live outside the [data-barba="container"] are updated.
                    syncManualBodyClasses(data.next.html, DEBUG_MODE);
                    syncPageStyles(data.next.html, DEBUG_MODE); // Sync styles before content is visible
                    syncPageModals(data.next.html, DEBUG_MODE); // Sync modals before content is visible
                    syncPageScriptsByLocation(data.next.html, ['head', 'body-start'], DEBUG_MODE); // Sync early scripts

                    // Reset scroll position. If Lenis is active, use it.
                    if (lenisInstance) {
                        lenisInstance.scrollTo(0, { immediate: true, force: true });
                    } else {
                        window.scrollTo(0, 0);
                    }
                },
                enter(data) {
                    if (DEBUG_MODE) console.log('[Barba Debug] enter: document.body.className AT START of enter:', document.body.className);
                    // The new content is already in the DOM, no fade-in needed.
                    if (DEBUG_MODE) console.log('[Barba Debug] enter: document.body.className AT END of enter:', document.body.className);
                },
                async afterEnter(data) {
                    if (DEBUG_MODE) {
                        console.log('[Barba Debug] afterEnter: document.body.className AT START of afterEnter (BEFORE initializePageScripts):', document.body.className);
                    }

                    // Sync scripts intended for the end of the body, like page-specific data or analytics, before the page is initialized.
                    syncPageScriptsByLocation(data.next.html, ['body-end'], DEBUG_MODE);

                    // Manually refresh Gravity Forms scripts
                    refreshGravityForms(data.next.html);

                    initializePageScripts(data); // Initialize scripts for the newly entered page

                    if (DEBUG_MODE) {
                        console.log('[Barba Debug] afterEnter: document.body.className AFTER initializePageScripts:', document.body.className);
                    }
                    // Track page view with Google Analytics for the new page
                    trackPageView(data, DEBUG_MODE);

                    // Remove the loading class to hide the page loader
                    // This will start the fade-out animation.
                    document.body.classList.remove('is-loading');

                    // Wait for the fade-out animation (2000ms) to complete before Barba finishes.
                    // This ensures the loader is fully gone before the user can interact with the new page.
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }],

            // Optional: Define views for page-specific JavaScript logic
            // views: [
            //   {
            //     namespace: 'home', // Corresponds to data-barba-namespace="home"
            //     afterEnter(data) { /* Home-specific scripts, e.g., init home page slider */ }
            //   },
            //   {
            //     namespace: 'contact-page', // Example namespace from your PHP
            //     afterEnter(data) { /* Initialize contact form scripts */ }
            //   }
            // ]
        });
    });
});

// The `beforeunload` event is a good place to destroy persistent instances like Lenis
// to ensure a clean state if the user navigates away from the site entirely.
window.addEventListener('beforeunload', () => {
  destroyScrollManager();
});


/*
├── app.js                     // Main entry point, orchestrates initializations
├── core/                      // Core utilities, global initializers, polyfills
│   ├── animation-initializer.js // (was layout-animations.js - for AOS, simpleParallax init)
│   ├── console-support.js
│   ├── css-viewport-height.js
│   ├── is-touch.js
│   ├── media-helpers.js       // (for object-fit-cover.js, html-videos.js logic)
│   ├── obfuscate-links.js
│   ├── polyfills.js           // (for input-placeholder-polyfill, objectFitPolyfill global call)
│   ├── prevent-default-class-helper.js
│   ├── scroll-manager.js      // (was smooth-page-scroll.js - Lenis, global scroll logic)
│   ├── scroll-utils.js        // (for scroll-percentage.js)
│   ├── url-utils.js           // (for querystring-dragging.js)
│   └── web-fonts.js
├── ui/                        // UI components and interactions
│   ├── accordion.js
│   ├── header/                // Header-specific UI components
│   │   ├── interactive-header.js // Handles dropdowns and search panel
│   │   ├── enquire-button.js  // (was header-enquire-button.js)
│   │   ├── mobile-menu.js     // (was header-mobile-menu.js)
│   │   └── sticky.js          // (was header-sticky.js)
│   ├── image-compare.js
│   ├── link-buttons.js
│   ├── modal-manager.js       // (was modals.js - MicroModal init, exit-intent logic)
│   ├── slideshow-manager.js   // (was slideshow.js - Swiper specific logic)
│   ├── sticky-drawer.js
│   ├── tabs.js
│   └── video/                 // UI components related to video presentation
│       ├── modal-loader.js    // (was modal-video-loader.js)
│       └── slideshow-loader.js// (was slideshow-video-loader.js)
├── features/                  // Distinct site features, often combining UI and logic
│   ├── calendar.js            // (from custom/)
│   ├── careers.js             // (from custom/)
│   ├── count-up.js            // (from custom/)
│   ├── faqs.js
│   ├── refine-select-filters.js
│   ├── scroll-window-effects.js // (was scroll-window.js and its animations)
│   └── url-jump-select.js
├── integrations/              // Third-party service/library integrations
│   ├── ajax-load-more-init.js // (was ajax-load-more.js, assuming it's an init script)
│   ├── google-maps-manager.js // (was google-maps.js)
│   ├── gravityforms-helpers.js// (was gravityforms-fileupload.js)
│   └── iframe-resizer-init.js // (was iframe-resize.js)
└── vendor/                    // (If any 3rd party libs are manually included and not via npm)
    └── ...
*/